import * as URI from 'urijs';

import { AssetUtils } from '®shared/asset-utils';
import {
  ALL_BIBLIOGRAPHIC_RESOURCE_TYPES,
  BibliographicResource,
  BibliographicResourceType,
} from '®shared/bibliographic-resource';
import { cleanupIsbn, isbn10ToIsbn13 } from '®shared/isbns/isbn-utils';

const BOOKJACKETS_BASE_URI = 'https://jackets.talis.com';

export const BOOKJACKET_IMAGE_WIDTH_SMALL = 100;
export const BOOKJACKET_IMAGE_WIDTH_LARGE = 400;

export class ResourceImageUtilsClass {
  /**
   * Return a bookjacket or placeholder image for a resource.
   *
   * @param {?BibliographicResource} resource - a resource object
   * @param {?number} jacketSize - the (width&height) of jacket image, in pixels
   * @returns {?string} - the resource image URL
   **/
  buildPlaceholderImageURL(resource?: BibliographicResource, jacketSize = 100) {
    const resourceType: BibliographicResourceType =
      (resource && resource.resource_type) || undefined;

    if (
      resourceType &&
      ALL_BIBLIOGRAPHIC_RESOURCE_TYPES.indexOf(resourceType) > -1
    ) {
      if (resourceType === BibliographicResourceType.Book) {
        const isbn = this.bestMatchingISBN13(resource);
        if (isbn) {
          return this.jacketsUrl(isbn, jacketSize);
        }
      } else if (
        resourceType === BibliographicResourceType.Chapter &&
        resource.part_of
      ) {
        const isbn = this.bestMatchingISBN13(resource.part_of);
        if (isbn) {
          return this.jacketsUrl(isbn, jacketSize);
        }
      }

      return this.placeholdersUrl(resourceType);
    }

    return undefined;
  }

  /**
   * Get the URL for a book identified by ISBN (from Metatron-Prime).
   *
   * @param {string} isbn - the ISBN(13) for the resource being looked up
   * @param {number} size - the pixel size of the jacket image being requested
   * @return {string} a URL for a book jacket
   */
  jacketsUrl(isbn: string, jacketSize: number): string {
    return new URI(BOOKJACKETS_BASE_URI)
      .segment(['manifestations', isbn, 'jackets', String(jacketSize)])
      .suffix('jpg')
      .toString();
  }

  /**
   * Get the URL for a placeholder image, appropriate to the image name.
   *
   * @param {string} imageName - the name of the image
   * @return {string} a URL for a placeholder image
   */
  placeholdersUrl(imageName: string): string {
    return AssetUtils.getAssetBaseUri()
      .segment(`shipshape-assets/resource-placeholders/${imageName}`)
      .suffix('svg')
      .toString();
  }

  /**
   * Return the best-matching ISBN13 for a resource.
   *
   * This performs normalisation on ISBN13s, then falls back to normalised and
   * converted ISBN10s.
   *
   * @param {?BibliographicResource} resource - the resource in question
   * @returns {?string} - the best-matching ISBN13
   **/
  bestMatchingISBN13(resource?: BibliographicResource) {
    if (resource) {
      // fetch and clean ISBN13s
      const isbn13s = resource && resource.isbn13s;
      const cleanIsbn13s =
        isbn13s && isbn13s.map(isbn => cleanupIsbn(isbn)).filter(isbn => isbn);

      // if an ISBN13 is available, return it
      const [bestIsbn13 = undefined] = cleanIsbn13s || [];
      if (bestIsbn13) {
        return bestIsbn13;
      }

      // fetch and convert ISBN10s to ISBN13s
      const isbn10s = resource && resource.isbn10s;
      const cleanIsbn10s =
        isbn10s &&
        isbn10s.map(isbn => isbn10ToIsbn13(isbn)).filter(isbn => isbn);

      // if a converted ISBN10 is available, return it
      const [bestIsbn10 = undefined] = cleanIsbn10s || [];
      if (bestIsbn10) {
        return bestIsbn10;
      }
    }
    return undefined;
  }
}

export const ResourceImageUtils = new ResourceImageUtilsClass();
